import React, { useState, useEffect } from 'react';
import { usersApi, DashboardStats } from '../../api/users';
import { Loader2 } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { cn } from '../../lib/utils';
import { Card } from '../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';

interface ChartsProps {
  isDark: boolean;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color?: string;
    fill?: string;
  }>;
  label?: string;
}

export default function Charts({ isDark }: ChartsProps) {
  const [chartType, setChartType] = useState<'registrations' | 'payments' | 'both'>('both');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await usersApi.getDashboardStats();
        setStats(data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Не удалось загрузить данные графика');
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={cn(
          'px-4 py-3 rounded-xl shadow-xl border',
          isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white border-slate-200'
        )}>
          <p className={cn(
            'text-sm font-semibold mb-2',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Day {label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-2.5 h-2.5 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                {entry.name === 'registrations' ? 'Регистрации' : entry.name === 'successfulPayments' ? 'Успешные платежи' : entry.name}: 
              </span>
              <span className={cn(
                'font-semibold',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>
                {entry.value?.toLocaleString() || 0}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const BarTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={cn(
          'px-4 py-3 rounded-xl shadow-xl border',
          isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white border-slate-200'
        )}>
          <p className={cn(
            'text-sm font-semibold mb-2',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-2.5 h-2.5 rounded-full" 
                style={{ backgroundColor: entry.fill }}
              />
              <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                {entry.name === 'current' ? 'Текущий период' : entry.name === 'previous' ? 'Прошлый период' : entry.name}: 
              </span>
              <span className={cn(
                'font-semibold',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>
                {entry.value?.toLocaleString() || 0}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
        <Card className={cn(
          'xl:col-span-2 p-5 lg:p-6 rounded-2xl border-0',
          isDark ? 'bg-[#1A1A22]' : 'bg-white'
        )}>
          <div className="flex items-center justify-center h-[320px]">
            <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
          </div>
        </Card>
        <Card className={cn(
          'p-5 lg:p-6 rounded-2xl border-0',
          isDark ? 'bg-[#1A1A22]' : 'bg-white'
        )}>
          <div className="flex items-center justify-center h-[320px]">
            <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
          </div>
        </Card>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
        <Card className={cn(
          'xl:col-span-2 p-5 lg:p-6 rounded-2xl border-0',
          isDark ? 'bg-[#1A1A22]' : 'bg-white'
        )}>
          <p className={cn(
            'text-sm',
            isDark ? 'text-red-400' : 'text-red-600'
          )}>
            {error || 'Не удалось загрузить данные графика'}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
      {/* Line Chart - Visits/Revenue over 30 days */}
      <Card className={cn(
        'xl:col-span-2 p-5 lg:p-6 rounded-2xl border-0 transition-all duration-300',
        isDark 
          ? 'bg-[#1A1A22] shadow-lg shadow-black/10' 
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className={cn(
              'text-lg font-semibold',
              isDark ? 'text-white' : 'text-[#111111]'
            )}>Активность по дням</h3>
            <p className={cn(
              'text-sm mt-0.5',
              isDark ? 'text-slate-400' : 'text-slate-500'
            )}>Регистрации и успешные платежи (последние 30 дней)</p>
          </div>
          <Tabs value={chartType} onValueChange={(value) => setChartType(value as 'registrations' | 'payments' | 'both')}>
            <TabsList className={cn(
              'rounded-xl p-1',
              isDark ? 'bg-slate-800' : 'bg-slate-100'
            )}>
              <TabsTrigger 
                value="registrations" 
                className={cn(
                  'rounded-lg text-sm px-4',
                  isDark && 'data-[state=active]:bg-[#C9A227] data-[state=active]:text-white'
                )}
              >
                Registrations
              </TabsTrigger>
              <TabsTrigger 
                value="payments"
                className={cn(
                  'rounded-lg text-sm px-4',
                  isDark && 'data-[state=active]:bg-[#C9A227] data-[state=active]:text-white'
                )}
              >
                Payments
              </TabsTrigger>
              <TabsTrigger 
                value="both"
                className={cn(
                  'rounded-lg text-sm px-4',
                  isDark && 'data-[state=active]:bg-[#C9A227] data-[state=active]:text-white'
                )}
              >
                Both
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        <div className="h-[280px] lg:h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.dailyActiveUsers} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={isDark ? '#334155' : '#E2E8F0'}
                vertical={false}
              />
              <XAxis 
                dataKey="day" 
                stroke={isDark ? '#64748B' : '#94A3B8'}
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke={isDark ? '#64748B' : '#94A3B8'}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => value >= 1000 ? `${value/1000}k` : String(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              {(chartType === 'registrations' || chartType === 'both') && (
                <Line
                  type="monotone"
                  dataKey="registrations"
                  name="registrations"
                  stroke="#C9A227"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: '#C9A227', strokeWidth: 2, stroke: isDark ? '#1A1A22' : '#fff' }}
                />
              )}
              {(chartType === 'payments' || chartType === 'both') && (
                <Line
                  type="monotone"
                  dataKey="successfulPayments"
                  name="successfulPayments"
                  stroke="#6366F1"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: '#6366F1', strokeWidth: 2, stroke: isDark ? '#1A1A22' : '#fff' }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Bar Chart - Channels */}
      <Card className={cn(
        'p-5 lg:p-6 rounded-2xl border-0 transition-all duration-300',
        isDark 
          ? 'bg-[#1A1A22] shadow-lg shadow-black/10' 
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        <div className="mb-6">
          <h3 className={cn(
            'text-lg font-semibold',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Активные пользователи по периодам</h3>
          <p className={cn(
            'text-sm mt-0.5',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Daily, Weekly, Monthly comparison</p>
        </div>
        
        <div className="h-[280px] lg:h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.activeUsersByPeriod} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={isDark ? '#334155' : '#E2E8F0'}
                vertical={false}
              />
              <XAxis 
                dataKey="name"
                stroke={isDark ? '#64748B' : '#94A3B8'}
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke={isDark ? '#64748B' : '#94A3B8'}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => value >= 1000 ? `${value/1000}k` : String(value)}
              />
              <Tooltip content={<BarTooltip />} />
              <Bar 
                dataKey="current" 
                name="current"
                fill="#C9A227" 
                radius={[6, 6, 0, 0]}
                barSize={40}
              />
              <Bar 
                dataKey="previous" 
                name="previous"
                fill="#6366F1" 
                radius={[6, 6, 0, 0]}
                barSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#C9A227]" />
            <span className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Текущий период</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#6366F1]" />
            <span className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Прошлый период</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

